import React, { useState, useEffect, useMemo, useCallback, memo } from 'react'
import { PlusOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import { ReactComponent as EditIcon } from "../../assets/Icons/Editicon.svg";
import ReusableTable from "../../Reuseable/ReusableTable/ReusableTable";
import PanelCard from "../DashboardPage/components/PanelCard";
import "./CallScriptPage.scss";
import { useDispatch, useSelector } from 'react-redux';
import CustomNotification from "../../Reuseable/CustomNotification/CustomNotification";
import { Skeleton, Space, Button, Tooltip, Tag } from 'antd';
import AddScriptModal from '../../components/CallScriptComp/AddScriptModal/AddScriptModal';
import { fetchCallScripts } from '../../store/callscriptsSlice';
import moment from 'moment';
import { AddCallScript, DeleteCallScript, EditCallScript } from '../../assets/api/ScriptsService';
import EditScriptModal from '../../components/CallScriptComp/EditScriptModal/EditScriptModal';
import DeleteCampaignModal from '../../components/CampaignComp/DeleteCampaignModal/DeleteCampaignModal';

const StaticSkeleton = () => <Skeleton.Input size="small" active />;

const BodyCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="scriptEmpty">—</span>;
  return (
    <Tooltip title={text} mouseEnterDelay={0.3}>
      <span className="scriptBody">{text}</span>
    </Tooltip>
  );
});

/* The .statusIndicator / .activeStatus classes are only styled inside
   ProductComp and UserList, so here antd's stock green/red Tag showed
   through. A dot chip, matching the status pills elsewhere. */
const StatusCell = memo(({ isActive, loading }) => {
  if (loading) return <StaticSkeleton />;
  return (
    <span className={`scriptStatus ${isActive ? "is-active" : "is-inactive"}`}>
      <i />
      {isActive ? "Active" : "Inactive"}
    </span>
  );
});

const CreatedAtCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="scriptEmpty">—</span>;
  const when = moment(text);
  if (!when.isValid()) return <span className="scriptEmpty">—</span>;
  return (
    <span className="scriptDate">
      <span className="scriptDate__day">{when.format("DD MMM YYYY")}</span>
      <span className="scriptDate__time">{when.format("hh:mm A")}</span>
    </span>
  );
});

const CallScriptPage = () => {
    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editScriptData, setEditScriptData] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteCallScriptId, setDeleteCallScriptId] = useState(null);
    const [loadSubmit, setLoadSubmit] = useState(false);

    const { callscripts_list, callscripts_list_loading } = useSelector((state) => state.callscripts);
    // const { filter_list } = useSelector((state) => state.filters);
    const { token } = useSelector((state) => state.auth);
    const [isAdding, setIsAdding] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const loading = callscripts_list_loading;

    
    const listData = callscripts_list?.data?.results || callscripts_list?.data?.scripts;
    
    const finalListData = useMemo(() => {
        return Array.isArray(listData) ? listData : [];
    }, [listData]);

    console.log(finalListData, "====Final Callscripts====");
    
    useEffect(() => {
        dispatch(fetchCallScripts({ page: currentPage, page_size: pageSize }));
    }, [dispatch, currentPage, pageSize]);

    const handleEditClick = useCallback((record) => {
      setEditScriptData(record);
      setIsEditModalOpen(true);
      console.log("Edit clicked for:", record);
    }, []);

    const handleDeleteClick = useCallback((record) => {
      console.log("Delete clicked for:", record);
      setDeleteCallScriptId(record.id);
      setIsDeleteModalOpen(true);
    }, []);

    const handleTableChange = useCallback((page, size) => {
      setCurrentPage(page);
      setPageSize(size);
    }, []);

    const handleAddModalOpen = useCallback(() => {
      setIsModalOpen(true);
    }, []);

    const handleAddModalClose = useCallback(() => {
      setIsModalOpen(false);
    }, []);

    const handleEditModalClose = useCallback(() => {
      setIsEditModalOpen(false);
      setEditScriptData(null);
    }, []);

    const handleDeleteModalClose = useCallback(() => {
      setIsDeleteModalOpen(false);
      setDeleteCallScriptId(null);
    }, []);

    const handleAddScriptSave = useCallback(async (values) => {
        try {
            setIsAdding(true);
            const res = await AddCallScript(token, values);
            if (res.status === 201 || res.status === 200) {
                CustomNotification.show("Script added successfully", "success");
                setIsModalOpen(false);
                dispatch(fetchCallScripts({ page: currentPage, page_size: pageSize }));
            }
        } catch (error) {
            CustomNotification.show(error || "Failed to add script", "error");
        } finally {
            setIsAdding(false);
        }
    }, [token, currentPage, pageSize, dispatch]);

    const handleEditScriptSave = useCallback(async (values) => {
        try {
            setIsUpdating(true);
            const res = await EditCallScript(token, editScriptData?.id, values);
            if (res.status === 200 || res.status === 201) {
                CustomNotification.show("Script updated successfully", "success");
                setIsEditModalOpen(false);
                setEditScriptData(null);
                dispatch(fetchCallScripts({ page: currentPage, page_size: pageSize }));
            }
        } catch (error) {
            CustomNotification.show(error || "Failed to update script", "error");
        } finally {
            setIsUpdating(false);
        }
    }, [token, editScriptData?.id, currentPage, pageSize, dispatch]);

    const handleDeleteScriptConfirm = useCallback(async () => {
        try {
            setLoadSubmit(true);
            const res = await DeleteCallScript(token, deleteCallScriptId);
            if (res.status === 200 || res.status === 201) {
                CustomNotification.show("Script deleted successfully", "success");
                setIsDeleteModalOpen(false);
                setDeleteCallScriptId(null);
                dispatch(fetchCallScripts({ page: currentPage, page_size: pageSize }));
            }
        } catch (error) {
            CustomNotification.show(error || "Failed to delete script", "error");
        } finally {
            setLoadSubmit(false);
            setDeleteCallScriptId(null);
            setIsDeleteModalOpen(false);
        }
    }, [token, deleteCallScriptId, currentPage, pageSize, dispatch]);

    const columns = useMemo(() => [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (text) =>
              loading ? (
                <StaticSkeleton />
              ) : (
                <span className="scriptName">{text || "—"}</span>
              ),
        },
        {
            title: "Body",
            dataIndex: "body",
            key: "body",
            render: (text) => <BodyCell text={text} loading={loading} />,
        },
        {
            title: "Status",
            dataIndex: "is_active",
            key: "is_active",
            render: (is_active) => <StatusCell isActive={is_active} loading={loading} />,
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (text) => <CreatedAtCell text={text} loading={loading} />,
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 96,
            render: (_, record) => {
              if (loading) return <StaticSkeleton />;
              return (
                <Space size={8}>
                  <Tooltip title="Edit script">
                    <Button
                      className="scriptIconBtn is-edit"
                      icon={<EditIcon />}
                      onClick={() => handleEditClick(record)}
                    />
                  </Tooltip>
                  <Tooltip title="Delete script">
                    <Button
                      className="scriptIconBtn is-delete"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteClick(record)}
                    />
                  </Tooltip>
                </Space>
              );
            },
          },
    ], [loading, handleEditClick, handleDeleteClick]);

    const skeletonData = useMemo(() => 
      Array(5).fill({}).map((_, i) => ({ id: `skeleton-${i}` })),
      []
    );

    const totalItems = useMemo(() => 
      callscripts_list?.data?.count !== undefined
        ? callscripts_list?.data?.count
        : (callscripts_list?.data?.total_count || finalListData.length),
      [callscripts_list, finalListData]
    );

  return (
    <div className="callscriptsPageMainWrapper">
      <AddScriptModal
        isOpen={isModalOpen}
        onClose={handleAddModalClose}
        load={isAdding}
        onSave={handleAddScriptSave}
      />

      <EditScriptModal 
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        load={isUpdating}
        scriptData={editScriptData}
        onSave={handleEditScriptSave}
      />

      <DeleteCampaignModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        loading={loadSubmit}
        onDelete={handleDeleteScriptConfirm}
        /* The shared dialog asks about a campaign by default; this page
           deletes a script. */
        message="Are you sure you want to delete this script?"
      />

      <PanelCard
        title="All Scripts"
        icon={<FileTextOutlined />}
        accent="brand"
        className="callscriptsContent"
        actions={
          <Button
            className="addScriptsBtn"
            icon={<PlusOutlined />}
            onClick={handleAddModalOpen}
          >
            Add Script
          </Button>
        }
      >
        <ReusableTable
          columns={columns}
          dataSource={loading ? skeletonData : finalListData}
          totalItems={totalItems}
          rowKey="id"
          CurrentPageNo={currentPage}
          pageSize={pageSize}
          triggerChange={handleTableChange}
        />
      </PanelCard>
    </div>
  );
}

export default memo(CallScriptPage);